import { graphql } from 'react-relay';
import React from 'react';
import { useLazyLoadQuery } from 'react-relay';
import classes from './ReferralInvites.module.scss';
import { ReferralInvitesQuery } from './__generated__/ReferralInvitesQuery.graphql';

import { BALANCE, BLOG_POSTS } from '../pages';
import A from '../../atoms/A/A';
import REFERRAL_INVITE_TYPES from './ReferralInviteType';
import ReferralInviteCard from './ReferralInviteCard';
import OinqDuo from '../../assets/oinq/oinqhandinhand.svg';
import ItemList from '../../molecules/ItemList/ItemList';
import Txt from '../../atoms/Txt/Txt';

export interface Props {}

const query = graphql`
  query ReferralInvitesQuery {
    referralInvites {
      edges {
        node {
          inviteType
          label
          token
        }
      }
    }
    userData {
      currency {
        currencySymbol
      }
    }
  }
`;

const ReferralInvites: React.FC<Props> = () => {
  const data = useLazyLoadQuery<ReferralInvitesQuery>(query, {});
  const currencySymbol = data.userData?.currency.currencySymbol;

  if (data.referralInvites && data.referralInvites.edges.length > 0) {
    const invitesMap = new Map<string, string>();
    data.referralInvites.edges.map((edge) => invitesMap.set(edge.node.inviteType, edge.node.token));

    return (
      <section className={classes.root}>
        <Txt component="h1" className={classes.title}>
          Refer a friend to Qmee
        </Txt>
        <Txt component="p">
          For every friend that signs up to use Qmee through one of your links, a{' '}
          <Txt component="span" skipTranslation>
            {currencySymbol}1
          </Txt>{' '}
          reward will be earned on their first cash out by PayPal or gift card!
        </Txt>
        <Txt component={A} to={BALANCE.path}>
          See your Referral Rewards
        </Txt>
        <Txt component="p">Remember...</Txt>
        <ol>
          <li>
            <Txt component="p">
              The person you refer has to sign up with the link we provide to you, and they must
              sign up through the website (either via a desktop or mobile).
            </Txt>
            <Txt className={classes.highlight} component="b">
              *Please be aware, your link won&apos;t work through the Qmee mobile app - so make sure
              the person you refer doesn&apos;t sign up using the app.
            </Txt>
          </li>
          <Txt component="li">
            You will only receive your referral bonus when they{' '}
            <Txt component="b">cash out via PayPal or Gift Card</Txt>
          </Txt>
          <Txt component="li">
            You are only permitted to have one account of your own - you can&apos;t refer yourself!
          </Txt>
        </ol>
        <Txt component="p">
          Choose what happens to your{' '}
          <Txt component="span" skipTranslation>
            {currencySymbol}1
          </Txt>{' '}
          reward (Keep, Share or Give!) by deciding which of your links to use...
        </Txt>
        <ItemList>
          {REFERRAL_INVITE_TYPES.map((invite) =>
            invitesMap.get(invite.inviteType) ? (
              <ReferralInviteCard
                key={invite.inviteType}
                token={invitesMap.get(invite.inviteType)!}
                invite={invite}
                currencySymbol={currencySymbol!}
              />
            ) : null,
          )}
        </ItemList>
        <div className={classes.cardContainer}>
          <div className={classes.innerContainer} />
        </div>
      </section>
    );
  }
  return (
    <section className={classes.noData}>
      <Txt component="p">
        Invites aren&apos;t available to you just yet but take a look at our blog to find out more
        more information about{' '}
        <Txt component={A} to={`${BLOG_POSTS.path}/how-to-invite-friends`}>
          inviting your friends
        </Txt>{' '}
        to Qmee.
      </Txt>
      <Txt component="p">
        If you have any queries, please contact our Support team on{' '}
        <A to="mailto:support@qmee.com">support@qmee.com</A>
      </Txt>
      <OinqDuo />
    </section>
  );
};

export default ReferralInvites;
